import { useEffect, useState } from "react";
import { speak, stopSpeaking, ttsSupported } from "../../../lib/tts";

/**
 * BD3.1 — "Listen" toggle under an assistant message. Uses the free Web
 * Speech API via lib/tts.ts (markdown stripped; tables summarised as a
 * spoken pointer). Renders nothing when the browser has no speechSynthesis
 * (honest empty state — no dead button). One message speaks at a time;
 * unmounting stops speech so navigation never leaves a ghost voice.
 */
export function SpeakButton({ text }: { text: string }) {
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    // Stop on unmount — leaving /chat mid-sentence must silence the voice.
    return () => {
      if (speaking) stopSpeaking();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ttsSupported() || !text.trim()) return null;

  function toggle() {
    if (speaking) {
      stopSpeaking();
      setSpeaking(false);
      return;
    }
    const handle = speak(text, () => setSpeaking(false));
    if (handle) setSpeaking(true);
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={speaking}
      aria-label={speaking ? "Stop reading aloud" : "Read this answer aloud"}
      data-testid="speak-button"
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1",
        "text-[11.5px] font-medium transition-colors",
        speaking
          ? "bg-[var(--color-brand-soft)] text-[var(--color-brand-deep)]"
          : "text-[var(--color-ink-40)] hover:text-[var(--color-ink)] hover:bg-[var(--color-ink-08)]",
      ].join(" ")}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M11 5 6 9H3v6h3l5 4V5z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        {speaking ? (
          <path
            d="M16 9v6M19.5 7v10"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        ) : (
          <path
            d="M15.5 8.5a5 5 0 0 1 0 7M18 6a8.5 8.5 0 0 1 0 12"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        )}
      </svg>
      {speaking ? "Stop" : "Listen"}
    </button>
  );
}

export default SpeakButton;
